/**
 * Build O(1) lookup maps for Domain Mapping / KPI Registry joins.
 */
export function buildConfigureIndexes({
    businesses = [],
    functions = [],
    kpis = [],
    models = [],
    assets = [],
    mappings = []
} = {}) {
    const businessById = new Map(businesses.map(b => [b.id, b]));
    const functionById = new Map(functions.map(f => [f.id, f]));
    const kpiById = new Map(kpis.map(k => [k.id, k]));
    const modelById = new Map(models.map(m => [m.id, m]));
    const assetById = new Map(assets.map(a => [a.id, a]));

    const functionsByBusinessId = new Map();
    functions.forEach(f => {
        if (!functionsByBusinessId.has(f.businessId)) functionsByBusinessId.set(f.businessId, []);
        functionsByBusinessId.get(f.businessId).push(f);
    });

    const kpisByFunctionId = new Map();
    kpis.forEach(k => {
        if (!kpisByFunctionId.has(k.functionId)) kpisByFunctionId.set(k.functionId, []);
        kpisByFunctionId.get(k.functionId).push(k);
    });

    const modelsByFunctionId = new Map();
    models.forEach(m => {
        if (!modelsByFunctionId.has(m.functionId)) modelsByFunctionId.set(m.functionId, []);
        modelsByFunctionId.get(m.functionId).push(m);
    });

    const mappingCountByKpiId = new Map();
    const mappingCountByModelId = new Map();
    const mappingByKpiAsset = new Map();

    mappings.forEach(m => {
        if (m.kpiId) {
            mappingCountByKpiId.set(m.kpiId, (mappingCountByKpiId.get(m.kpiId) || 0) + 1);
            if (m.assetId) {
                mappingByKpiAsset.set(`${m.kpiId}::${m.assetId}`, m);
            }
        }
        if (m.modelId) {
            mappingCountByModelId.set(m.modelId, (mappingCountByModelId.get(m.modelId) || 0) + 1);
        }
    });

    const getBiz = id => businessById.get(id);
    const getFn = id => functionById.get(id);
    const getKpi = id => kpiById.get(id);
    const getModel = id => modelById.get(id);
    const getAsset = id => assetById.get(id);

    const isBizEff = b => !!(b && b.active);
    const isFnEff = f => !!(f && f.active && isBizEff(getBiz(f.businessId)));
    const isKpiEff = k => !!(k && k.active && isFnEff(getFn(k.functionId)));
    const isModelEff = m => !!(m && m.active && isFnEff(getFn(m.functionId)));

    const resolveMappingContext = (mapping) => {
        const kpi = mapping.kpiId ? getKpi(mapping.kpiId) : null;
        const model = !kpi && mapping.modelId ? getModel(mapping.modelId) : null;
        const item = kpi || model;
        if (!item) return null;
        const fn = getFn(item.functionId);
        const biz = fn ? getBiz(fn.businessId) : null;
        const asset = mapping.assetId ? getAsset(mapping.assetId) : null;
        const effectivelyActive = kpi ? isKpiEff(kpi) : isModelEff(model);
        return { kpi, model, item, fn, biz, asset, effectivelyActive, isModel: !!model };
    };

    return {
        businessById,
        functionById,
        kpiById,
        modelById,
        assetById,
        functionsByBusinessId,
        kpisByFunctionId,
        modelsByFunctionId,
        mappingCountByKpiId,
        mappingCountByModelId,
        mappingByKpiAsset,
        getBiz,
        getFn,
        getKpi,
        getModel,
        getAsset,
        isBizEff,
        isFnEff,
        isKpiEff,
        isModelEff,
        resolveMappingContext,
        getFnsOf: bid => functionsByBusinessId.get(bid) || [],
        getKpisOf: fid => kpisByFunctionId.get(fid) || [],
        getModelsOf: fid => modelsByFunctionId.get(fid) || [],
        getMappingCount: (itemId, isModel) =>
            isModel
                ? (mappingCountByModelId.get(itemId) || 0)
                : (mappingCountByKpiId.get(itemId) || 0)
    };
}

export function mappingPairKey(kpiId, assetId) {
    return `${kpiId}::${assetId}`;
}

/**
 * Upsert mappings by unique (kpiId, assetId).
 * Optionally remove one mapping (replaceMappingId) or all mappings for a KPI (replaceKpiId).
 */
export function upsertMappings(existingMappings, incomingMappings, { replaceMappingId = null, replaceKpiId = null } = {}) {
    const next = existingMappings.filter(m => {
        if (replaceKpiId && m.kpiId === replaceKpiId) return false;
        if (replaceMappingId && m.id === replaceMappingId) return false;
        return true;
    });
    const byPair = new Map();
    next.forEach(m => {
        if (m.kpiId && m.assetId) {
            byPair.set(mappingPairKey(m.kpiId, m.assetId), m);
        }
    });

    incomingMappings.forEach(incoming => {
        const key = mappingPairKey(incoming.kpiId, incoming.assetId);
        const existing = byPair.get(key);
        if (existing) {
            const idx = next.findIndex(m => m.id === existing.id);
            if (idx > -1) {
                next[idx] = { ...existing, ...incoming, id: existing.id };
                byPair.set(key, next[idx]);
            }
        } else {
            next.push(incoming);
            byPair.set(key, incoming);
        }
    });

    return next;
}
